import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import OptionButtons from "./components/OptionButtons";
import RendersBottomBar from "./components/RendersBottomBar";
import Header from "./components/TopBar";
import Viewer360 from "./components/viewer360";
import { Render, getCompletedRenders } from "./services/renders-api";

interface UserFolderLayoutProps {
  renders: Render[];
  loading?: boolean;
  error?: string | null;
}

/**
 * User Folder Layout Component
 * 
 * Layout for displaying rendered images for a user folder.
 * Similar structure to Layout.tsx but without 3D configurator,
 * shows selected render image in main content area.
 */
const UserFolderLayout: React.FC<UserFolderLayoutProps> = ({
  renders,
  loading = false,
  error = null,
}) => {
  const [selectedRender, setSelectedRender] = useState<Render | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const viewer360Ref = useRef<HTMLDivElement>(null);

  // Get completed renders for navigation
  const completedRenders = useMemo(() => getCompletedRenders(renders), [renders]);

  // Fullscreen state management
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Auto-select first completed render if none selected
  useMemo(() => {
    if (!selectedRender && completedRenders.length > 0) {
      setSelectedRender(completedRenders[0]);
      setCurrentIndex(0);
    }
  }, [completedRenders, selectedRender]);

  const handleRenderSelect = (render: Render) => {
    setSelectedRender(render);
    const index = completedRenders.findIndex((r) => r.id === render.id);
    if (index !== -1) {
      setCurrentIndex(index);
    }
  };

  const handleClickOnNext = (direction: "left" | "right") => {
    if (completedRenders.length === 0) return;

    let newIndex: number;
    if (direction === "left") {
      newIndex = currentIndex > 0 ? currentIndex - 1 : completedRenders.length - 1;
    } else {
      newIndex = currentIndex < completedRenders.length - 1 ? currentIndex + 1 : 0;
    }

    setCurrentIndex(newIndex);
    setSelectedRender(completedRenders[newIndex]);
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      const target = selectedRender?.type === '360'
        ? viewer360Ref.current
        : imageRef.current;
      target?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  // Determine what to show in main content
  const renderMainContent = () => {
    if (loading) {
      return <PlaceholderText>Chargement...</PlaceholderText>;
    }

    if (error) {
      return <PlaceholderText>{error}</PlaceholderText>;
    }

    if (!selectedRender) {
      if (renders.length === 0) {
        return <PlaceholderText>Aucun rendu disponible</PlaceholderText>;
      }
      // Renders exist but none completed yet
      return <PlaceholderText>Rendus en cours de traitement...</PlaceholderText>;
    }

    // Show 360 viewer for 360 renders, otherwise show image
    if (selectedRender.type === '360' && selectedRender.imageUrl) {
      return <ImageContainer>
        <Viewer360 ref={viewer360Ref} url={selectedRender.imageUrl} isFullscreen={isFullscreen} />
        <OptionButtons url={selectedRender.imageUrl}
          onFullscreenToggle={toggleFullscreen}
          isFullscreen={isFullscreen}
          toggleDownloadButton={false}
        />
      </ImageContainer>;
    }

    if (selectedRender.imageUrl) {
      return (
        <ImageContainer><RenderImage
          ref={imageRef}
          src={selectedRender.imageUrl}
          alt={selectedRender.name} />
          <OptionButtons url={selectedRender.imageUrl} onFullscreenToggle={toggleFullscreen} isFullscreen={isFullscreen} />
        </ImageContainer>
      );
    }
  

    return <PlaceholderText>Image non disponible</PlaceholderText>;
  };

  const showArrows = completedRenders.length > 1;

  return (
    <Container>
      <ContentWrapper>
        <Header/>
        <MainContent>
          {renderMainContent()}
          {showArrows && (
            <>
              <ArrowButton direction="left" onClick={() => handleClickOnNext("left")} />
              <ArrowButton direction="right" onClick={() => handleClickOnNext("right")} />
            </>
          )}
          {selectedRender && (
            <RenderInfo>
              <RenderName>{selectedRender.name}</RenderName>
              <RenderType>{selectedRender.type === '360' ? '360°' : 'Image'}</RenderType>
            </RenderInfo>
          )}
        </MainContent>
        <RendersBottomBar
          renders={renders}
          loading={loading}
          error={error}
          selectedRenderId={selectedRender?.id}
          onRenderSelect={handleRenderSelect}
        />
      </ContentWrapper>
    </Container>
  );
};

// Styled components
const Container = styled.div`
  display: flex;
  height: 100vh;
  width: 100%;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ImageContainer = styled.div`
  display:flex;
`

const MainContent = styled.div`
  position: relative;
  overflow: hidden;
  flex: 1;
  background-color: #f0f0f0;
  background-size: 20px 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  @media (max-width: 1366px) {
    padding-bottom: 0;
    padding-top:0;
  }
`;

const RenderImage = styled.img`
  
  width: calc(min(80vh / 9 * 16, 80vw) * 0.8);
  height: calc(min(80vh, 80vw / 16 * 9) * 0.8);
  @media (max-width: 1376px) {
    width: calc(min(95vh / 9 * 16, 95vw) * 0.8);
    height: calc(min(95vh, 95vw / 16 * 9) * 0.8);
  }
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const PlaceholderText = styled.div`
  color: #666;
  font-size: 1.2rem;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

const RenderInfo = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  padding: 0.5rem 1rem;
  border-radius: 8px;
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const RenderName = styled.span`
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

const RenderType = styled.span`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.8rem;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

const ArrowButton = styled.div<{ direction: "left" | "right" }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${({ direction }) => (direction === "left" ? "left: 2rem;" : "right: 2rem;")}
  width: 3rem;
  height: 3rem;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  z-index: 10;

  &:hover {
    background: rgba(255, 255, 255, 1);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  }

  &::before {
    content: "";
    width: 1rem;
    height: 1rem;
    border-top: 2px solid #333;
    border-right: 2px solid #333;
    transform: ${({ direction }) =>
      direction === "left" ? "rotate(225deg)" : "rotate(45deg)"};
    transition: all 0.3s ease;
  }
`;

export default UserFolderLayout;
